/**
 * CoursPresentation.jsx — Mode Présentation Interactive Pro
 *
 * Route : /enseignant/cours-circulation/:slug/presenter
 *
 * Transforme une fiche cours (longue page scrollable) en présentation
 * SLIDE-PAR-SLIDE plein écran style studio TV avec :
 *   - Brise-glace d'ouverture interactif
 *   - 1 slide par section (objectifs, deroule[], cloture)
 *   - Reveal progressif des bullets (framer-motion)
 *   - QCM intercalés "Lève la main" avec vote, révélation animée et explication
 *   - Chiffres-chocs en compteur animé (style digital signage)
 *   - Timer optionnel par slide
 *   - Navigation clavier ← → Espace / Échap
 *   - Barre de progression élégante
 *   - Citation finale grand format
 */

import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X, ChevronLeft, ChevronRight, Pause, Play, Volume2, VolumeX, Sparkles,
  Target, Users, ClipboardCheck, Quote, Flame, Hand, Award,
  CheckCircle2, XCircle, BarChart3, Lightbulb, Video,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import useSeo from '../hooks/useSeo';
import useSoundEffects from '../hooks/useSoundEffects';
import { getCoursBySlug } from '../data/coursSessionCirculation';
import PASSERELLE_INTERACTIF from '../data/coursSessionCirculation/passerelle-a2-a-interactif';
import FORMATION_B125_INTERACTIF from '../data/coursSessionCirculation/formation-7h-b-125-interactif';
import PERMIS_AM_INTERACTIF from '../data/coursSessionCirculation/permis-am-bsr-interactif';

const INTERACTIF_MAP = {
  'passerelle-a2-a': PASSERELLE_INTERACTIF,
  'formation-7h-b-125': FORMATION_B125_INTERACTIF,
  'permis-am-bsr': PERMIS_AM_INTERACTIF,
};

/* ════════════════════════════════════════════════════════════════════
 * Construction des slides depuis la data du cours + couche interactive
 * ════════════════════════════════════════════════════════════════════ */

function buildSlides(cours, interactif) {
  const slides = [];

  // 0 — Slide TITRE
  slides.push({
    kind: 'title',
    title: cours.titre || cours.title || 'Cours',
    subtitle: cours.sousTitre || cours.subtitle,
    duree: cours.duree || cours.dureeTotal,
    icon: Flame,
    accent: '#cf2031',
  });

  // 0bis — Brise-glace d'ouverture (si présent)
  if (interactif?.briseGlaceOuverture) {
    slides.push({
      kind: 'qcm',
      title: interactif.briseGlaceOuverture.titre,
      consigne: interactif.briseGlaceOuverture.consigne,
      question: interactif.briseGlaceOuverture.question,
      options: interactif.briseGlaceOuverture.options,
      explication: interactif.briseGlaceOuverture.explication,
      accent: '#f59e0b',
      icon: Flame,
    });
  }

  // 1 — Objectifs (3 slides : Savoir / Savoir-faire / Savoir-être)
  if (cours.objectifs) {
    const objMap = [
      { key: 'savoir', label: 'SAVOIR', icon: Lightbulb, color: '#0891b2' },
      { key: 'savoirFaire', label: 'SAVOIR-FAIRE', icon: Target, color: '#16a34a' },
      { key: 'savoirEtre', label: 'SAVOIR-ÊTRE', icon: Award, color: '#9333ea' },
    ];
    objMap.forEach((o) => {
      if (cours.objectifs[o.key]) {
        slides.push({
          kind: 'objectif',
          title: o.label,
          icon: o.icon,
          accent: o.color,
          content: cours.objectifs[o.key],
        });
      }
    });
  }

  // 2 — Pour chaque étape du déroulé
  (cours.deroule || []).forEach((etape, idx) => {
    const interactifEtape = (interactif?.etapes || []).find((e) => e.etape === etape.etape);

    // Slide titre étape + animation principale
    slides.push({
      kind: 'etape',
      title: `Étape ${etape.etape}`,
      subtitle: etape.titre,
      duree: etape.duree,
      role: etape.role,
      icon: ClipboardCheck,
      accent: '#1d4ed8',
      animation: etape.animation,
    });

    // Slide brise-glace de l'étape
    if (interactifEtape?.briseGlace) {
      slides.push({
        kind: 'icebreaker',
        title: '🎤 Tour de parole',
        accent: '#ea580c',
        icon: Hand,
        prompt: interactifEtape.briseGlace,
      });
    }

    // Slide VIDEO (si fournie)
    if (interactifEtape?.videoUrl) {
      slides.push({
        kind: 'video',
        title: '🎥 Vidéo à projeter',
        accent: '#dc2626',
        icon: Video,
        videoUrl: interactifEtape.videoUrl,
        caption: interactifEtape.videoCaption || 'Discussion à chaud après le visionnage : qu\'avez-vous vu ? Qu\'aurait-il fallu faire ?',
      });
    }

    // Slide STAT-CHOC
    if (interactifEtape?.statClef) {
      slides.push({
        kind: 'stat',
        title: 'Chiffre-choc',
        accent: '#dc2626',
        icon: BarChart3,
        valeur: interactifEtape.statClef.valeur,
        label: interactifEtape.statClef.label,
        detail: interactifEtape.statClef.detail,
      });
    }

    // Slide QUESTIONS À POSER
    if (etape.questions_eleve && etape.questions_eleve.length > 0) {
      slides.push({
        kind: 'questions',
        title: '💬 Questions à poser',
        accent: '#0891b2',
        icon: Users,
        questions: etape.questions_eleve,
      });
    }

    // Slide QCM
    if (interactifEtape?.qcm) {
      slides.push({
        kind: 'qcm',
        title: `🎯 QCM — Étape ${etape.etape}`,
        consigne: 'Lève la main pour répondre',
        question: interactifEtape.qcm.question,
        options: interactifEtape.qcm.options,
        explication: interactifEtape.qcm.explication,
        accent: '#7c3aed',
        icon: Lightbulb,
      });
    }

    // Slide ATTENDU (fin d'étape)
    if (etape.attendu) {
      slides.push({
        kind: 'attendu',
        title: '✅ Attendu de cette étape',
        accent: '#16a34a',
        icon: CheckCircle2,
        attendu: etape.attendu,
      });
    }
  });

  // FINAL — Citation
  if (interactif?.citationFinale) {
    slides.push({
      kind: 'citation',
      accent: '#cf2031',
      icon: Quote,
      texte: interactif.citationFinale.texte,
      auteur: interactif.citationFinale.auteur,
    });
  }

  // Slide de FIN
  slides.push({
    kind: 'end',
    title: 'Bravo !',
    subtitle: 'Tu repars avec ton plan personnel.',
    accent: '#16a34a',
    icon: Award,
  });

  return slides;
}

/* ════════════════════════════════════════════════════════════════════
 *  Slide renderers
 * ════════════════════════════════════════════════════════════════════ */

function SlideShell({ slide, children, slideIndex }) {
  const Icon = slide.icon || Sparkles;
  return (
    <motion.div
      key={slideIndex}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.35, ease: 'easeOut' }}
      className="w-full h-full flex flex-col"
    >
      <div
        className="flex items-center gap-3 mb-6 px-4 py-2 rounded-2xl backdrop-blur"
        style={{ background: `${slide.accent}22`, border: `1px solid ${slide.accent}66` }}
      >
        <Icon className="w-5 h-5" style={{ color: slide.accent }} />
        <span className="text-sm font-black uppercase tracking-[0.2em] text-white/90">
          {slide.title}
        </span>
      </div>
      <div className="flex-1 flex flex-col justify-center">
        {children}
      </div>
    </motion.div>
  );
}

function TitleSlide({ slide }) {
  return (
    <SlideShell slide={slide}>
      <div className="text-center">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.15, duration: 0.5 }}
          className="inline-flex w-20 h-20 rounded-3xl items-center justify-center mb-6"
          style={{ background: slide.accent }}
        >
          <Flame className="w-10 h-10 text-white" strokeWidth={2.2} />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-5xl md:text-7xl font-black leading-tight text-white"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {slide.title}
        </motion.h1>
        {slide.subtitle && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="mt-4 text-xl md:text-3xl text-white/70 font-bold"
          >
            {slide.subtitle}
          </motion.p>
        )}
        {slide.duree && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="mt-6 inline-block px-4 py-2 rounded-full text-sm font-black uppercase tracking-widest text-white"
            style={{ background: `${slide.accent}33`, border: `2px solid ${slide.accent}` }}
          >
            ⏱ {slide.duree}
          </motion.div>
        )}
      </div>
    </SlideShell>
  );
}

function ObjectifSlide({ slide }) {
  return (
    <SlideShell slide={slide}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4 }}
        className="max-w-5xl mx-auto text-center"
      >
        <p className="text-3xl md:text-5xl font-black leading-tight text-white" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
          {slide.content}
        </p>
      </motion.div>
    </SlideShell>
  );
}

function EtapeSlide({ slide }) {
  return (
    <SlideShell slide={slide}>
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
        >
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="text-sm font-black uppercase tracking-widest text-white/60">
              {slide.title} · {slide.duree} · {slide.role}
            </span>
          </div>
          <h2 className="text-4xl md:text-6xl font-black leading-tight text-white mb-6" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            {slide.subtitle}
          </h2>
          {slide.animation && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-xl md:text-2xl leading-relaxed text-white/85"
            >
              {slide.animation}
            </motion.p>
          )}
        </motion.div>
      </div>
    </SlideShell>
  );
}

function IcebreakerSlide({ slide }) {
  return (
    <SlideShell slide={slide}>
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          animate={{ rotate: [-3, 3, -3] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="text-7xl mb-6"
        >
          🎤
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-3xl md:text-5xl font-black leading-tight text-white"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {slide.prompt}
        </motion.p>
      </div>
    </SlideShell>
  );
}

function StatSlide({ slide }) {
  return (
    <SlideShell slide={slide}>
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: 'spring', stiffness: 120, damping: 12 }}
          className="text-7xl md:text-[10rem] font-black leading-none mb-4"
          style={{
            color: slide.accent,
            textShadow: `0 0 60px ${slide.accent}66`,
            fontFamily: "'Source Code Pro', monospace",
          }}
        >
          {slide.valeur}
        </motion.div>
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-2xl md:text-3xl font-black text-white uppercase tracking-wider"
        >
          {slide.label}
        </motion.p>
        {slide.detail && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-4 text-base md:text-lg text-white/60"
          >
            {slide.detail}
          </motion.p>
        )}
      </div>
    </SlideShell>
  );
}

function QuestionsSlide({ slide }) {
  return (
    <SlideShell slide={slide}>
      <div className="max-w-4xl mx-auto">
        <div className="space-y-4">
          {slide.questions.map((q, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 + i * 0.15, duration: 0.4 }}
              className="flex items-start gap-3 p-4 rounded-2xl"
              style={{ background: `${slide.accent}22`, border: `1px solid ${slide.accent}55` }}
            >
              <span className="text-2xl shrink-0">❓</span>
              <p className="text-xl md:text-2xl font-bold text-white leading-snug italic">« {q} »</p>
            </motion.div>
          ))}
        </div>
      </div>
    </SlideShell>
  );
}

function VideoSlide({ slide }) {
  // Accepte un URL YouTube (watch?v=...) ou un embed direct
  const url = slide.videoUrl || '';
  let embedSrc = url;
  const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (match) {
    embedSrc = `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1`;
  }
  return (
    <SlideShell slide={slide}>
      <div className="max-w-5xl mx-auto w-full">
        <div className="aspect-video w-full rounded-2xl overflow-hidden border-2 border-white/10 shadow-2xl bg-black">
          {url ? (
            <iframe
              src={embedSrc}
              title="Vidéo pédagogique"
              className="w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
              data-testid="video-iframe"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/40 italic text-lg">
              📺 Insérez ici votre URL YouTube dans le data interactif
            </div>
          )}
        </div>
        {slide.caption && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            className="mt-4 text-center text-base md:text-lg text-white/70 italic"
          >
            {slide.caption}
          </motion.p>
        )}
      </div>
    </SlideShell>
  );
}

function QcmSlide({ slide, onReveal }) {
  const [picked, setPicked] = useState(null);
  const [revealed, setRevealed] = useState(false);
  const correctIdx = slide.options.findIndex((o) => o.correct);

  const handleReveal = () => {
    setRevealed(true);
    if (onReveal) onReveal(picked === correctIdx);
  };
  return (
    <SlideShell slide={slide}>
      <div className="max-w-5xl mx-auto">
        {slide.consigne && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-4 inline-block px-3 py-1 rounded-full text-xs font-black uppercase tracking-widest text-white/70 border border-white/20"
          >
            ✋ {slide.consigne}
          </motion.div>
        )}
        <motion.h2
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-3xl md:text-5xl font-black leading-tight text-white mb-8"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {slide.question}
        </motion.h2>
        <div className="grid sm:grid-cols-2 gap-3" data-testid="qcm-options">
          {slide.options.map((opt, i) => {
            const isPicked = i === picked;
            const isCorr = i === correctIdx;
            let bg = 'rgba(255,255,255,0.05)';
            let border = 'rgba(255,255,255,0.2)';
            if (revealed) {
              if (isCorr) { bg = 'rgba(22,163,74,0.25)'; border = '#16a34a'; }
              else if (isPicked) { bg = 'rgba(220,38,38,0.25)'; border = '#dc2626'; }
            } else if (isPicked) {
              bg = `${slide.accent}33`; border = slide.accent;
            }
            return (
              <motion.button
                key={`opt-${i}`}
                whileHover={!revealed ? { scale: 1.02 } : {}}
                whileTap={!revealed ? { scale: 0.98 } : {}}
                onClick={() => !revealed && setPicked(i)}
                disabled={revealed}
                data-testid={`qcm-opt-${i}`}
                className="text-left p-4 rounded-2xl border-2 transition-all"
                style={{ background: bg, borderColor: border }}
              >
                <div className="flex items-start gap-3">
                  <div
                    className="w-10 h-10 rounded-xl flex items-center justify-center text-white font-black shrink-0"
                    style={{ background: revealed && isCorr ? '#16a34a' : revealed && isPicked ? '#dc2626' : (isPicked ? slide.accent : 'rgba(255,255,255,0.1)') }}
                  >
                    {revealed && isCorr ? <CheckCircle2 className="w-5 h-5" /> : (revealed && isPicked && !isCorr ? <XCircle className="w-5 h-5" /> : String.fromCharCode(65 + i))}
                  </div>
                  <span className="text-lg md:text-xl font-bold text-white leading-snug pt-1.5">{opt.label}</span>
                </div>
              </motion.button>
            );
          })}
        </div>
        <AnimatePresence>
          {revealed && slide.explication && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="mt-6 p-4 rounded-2xl border-2 border-amber-400 bg-amber-500/10 flex items-start gap-3"
              data-testid="qcm-explication"
            >
              <Lightbulb className="w-6 h-6 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-base md:text-lg text-white leading-relaxed">{slide.explication}</p>
            </motion.div>
          )}
        </AnimatePresence>
        {!revealed && (
          <div className="mt-6 flex justify-center">
            <Button
              onClick={handleReveal}
              disabled={picked === null}
              className="gap-2 bg-white text-black hover:bg-white/90 font-black uppercase tracking-wider px-6 py-5 text-base"
              data-testid="qcm-reveal"
            >
              <Sparkles className="w-5 h-5" /> Révéler la réponse
            </Button>
          </div>
        )}
      </div>
    </SlideShell>
  );
}

function AttenduSlide({ slide }) {
  return (
    <SlideShell slide={slide}>
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          animate={{ scale: [1, 1.05, 1] }}
          transition={{ duration: 1, repeat: Infinity }}
          className="text-7xl mb-6"
        >
          ✅
        </motion.div>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-2xl md:text-4xl font-black leading-tight text-white"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {slide.attendu}
        </motion.p>
      </div>
    </SlideShell>
  );
}

function CitationSlide({ slide }) {
  return (
    <SlideShell slide={slide}>
      <div className="max-w-5xl mx-auto text-center">
        <Quote className="w-16 h-16 mx-auto mb-4" style={{ color: slide.accent }} />
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-3xl md:text-5xl font-black leading-tight text-white italic"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          « {slide.texte} »
        </motion.p>
        {slide.auteur && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="mt-6 text-base md:text-lg text-white/60 uppercase tracking-widest font-bold"
          >
            — {slide.auteur}
          </motion.p>
        )}
      </div>
    </SlideShell>
  );
}

function EndSlide({ slide }) {
  return (
    <SlideShell slide={slide}>
      <div className="max-w-4xl mx-auto text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 120 }}
          className="inline-flex w-24 h-24 rounded-3xl items-center justify-center mb-6"
          style={{ background: slide.accent }}
        >
          <Award className="w-12 h-12 text-white" strokeWidth={2.2} />
        </motion.div>
        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-5xl md:text-7xl font-black leading-tight text-white"
          style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
        >
          {slide.title}
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6 }}
          className="mt-4 text-xl md:text-3xl text-white/80 font-bold"
        >
          {slide.subtitle}
        </motion.p>
      </div>
    </SlideShell>
  );
}

const SLIDE_RENDERERS = {
  title: TitleSlide,
  objectif: ObjectifSlide,
  etape: EtapeSlide,
  icebreaker: IcebreakerSlide,
  stat: StatSlide,
  questions: QuestionsSlide,
  qcm: QcmSlide,
  video: VideoSlide,
  attendu: AttenduSlide,
  citation: CitationSlide,
  end: EndSlide,
};

/* ════════════════════════════════════════════════════════════════════
 *  Composant principal
 * ════════════════════════════════════════════════════════════════════ */

export default function CoursPresentation() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const cours = useMemo(() => getCoursBySlug(slug), [slug]);
  const interactif = INTERACTIF_MAP[slug] || null;
  const slides = useMemo(
    () => (cours ? buildSlides(cours, interactif) : []),
    [cours, interactif],
  );

  const [idx, setIdx] = useState(0);
  const containerRef = useRef(null);
  const sounds = useSoundEffects();

  const handleQcmReveal = useCallback((correct) => {
    if (correct) sounds.playSuccess();
    else sounds.playError();
  }, [sounds]);

  useSeo({
    title: cours
      ? `Présentation Pro · ${cours.titre || 'Cours'} — Espace enseignant`
      : 'Présentation cours',
    description: 'Mode présentation interactive plein écran avec QCM, brise-glace, statistiques animées et navigation au clavier.',
    path: `/enseignant/cours-circulation/${slug || ''}/presenter`,
  });

  // Navigation clavier
  const next = useCallback(() => setIdx((i) => Math.min(i + 1, slides.length - 1)), [slides.length]);
  const prev = useCallback(() => setIdx((i) => Math.max(i - 1, 0)), []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ' || e.key === 'PageDown') {
        e.preventDefault();
        next();
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        e.preventDefault();
        prev();
      } else if (e.key === 'Escape') {
        navigate(`/enseignant/cours-circulation/${slug}`);
      } else if (e.key === 'Home') {
        setIdx(0);
      } else if (e.key === 'End') {
        setIdx(slides.length - 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [next, prev, navigate, slug, slides.length]);

  // Lance fullscreen au montage
  useEffect(() => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen().catch(() => {});
    return () => {
      if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    };
  }, []);

  if (!cours) {
    return (
      <div className="min-h-screen bg-black text-white p-8 flex items-center justify-center" data-testid="cours-not-found">
        <div className="text-center">
          <h1 className="text-3xl font-black mb-4">Cours introuvable</h1>
          <Link to="/enseignant/cours-circulation">
            <Button variant="outline" className="gap-2">Retour</Button>
          </Link>
        </div>
      </div>
    );
  }

  const slide = slides[idx];
  const Renderer = SLIDE_RENDERERS[slide.kind] || TitleSlide;
  const progress = ((idx + 1) / slides.length) * 100;

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-[9999] overflow-hidden c2r-presentation-mode"
      style={{
        background: 'radial-gradient(ellipse at center, #1a1a1a 0%, #050505 100%)',
        color: 'white',
      }}
      data-testid="cours-presentation"
    >
      {/* HEADER */}
      <div className="absolute top-0 left-0 right-0 z-10 px-6 py-3 flex items-center gap-3 bg-black/50 backdrop-blur border-b border-white/10">
        <Link
          to={`/enseignant/cours-circulation/${slug}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-black uppercase tracking-wider transition-all"
          data-testid="btn-exit-presentation"
        >
          <X className="w-3.5 h-3.5" /> Quitter
        </Link>
        <span className="text-xs font-black uppercase tracking-widest text-white/50">
          {cours.titre || cours.title}
        </span>
        <div className="flex-1" />
        <button
          type="button"
          onClick={sounds.toggleMute}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/20 text-xs font-black uppercase tracking-wider transition-all"
          data-testid="btn-mute"
          aria-label={sounds.muted ? 'Activer les sons' : 'Couper les sons'}
        >
          {sounds.muted ? <VolumeX className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
          {sounds.muted ? 'Muet' : 'Sons'}
        </button>
        <span className="text-xs font-black tabular-nums text-white/70">
          {idx + 1} / {slides.length}
        </span>
      </div>

      {/* PROGRESS BAR */}
      <div className="absolute top-12 left-0 right-0 z-10 h-0.5 bg-white/10">
        <motion.div
          className="h-full"
          style={{ background: slide.accent || '#cf2031' }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.3 }}
        />
      </div>

      {/* SLIDE CONTENT */}
      <div className="absolute inset-0 pt-20 pb-20 px-8 lg:px-16 flex items-center justify-center">
        <AnimatePresence mode="wait">
          <div key={idx} className="w-full max-w-7xl">
            <Renderer slide={slide} onReveal={handleQcmReveal} />
          </div>
        </AnimatePresence>
      </div>

      {/* FOOTER NAV */}
      <div className="absolute bottom-0 left-0 right-0 z-10 px-6 py-4 flex items-center justify-between gap-3 bg-black/50 backdrop-blur border-t border-white/10">
        <button
          type="button"
          onClick={prev}
          disabled={idx === 0}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed text-sm font-black uppercase tracking-wider transition-all"
          data-testid="btn-prev"
        >
          <ChevronLeft className="w-4 h-4" /> Précédent
        </button>
        <span className="text-[10px] text-white/40 uppercase tracking-widest hidden md:inline">
          ← → flèches · Espace · Échap pour quitter
        </span>
        <button
          type="button"
          onClick={next}
          disabled={idx === slides.length - 1}
          className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:cursor-not-allowed text-sm font-black uppercase tracking-wider transition-all"
          data-testid="btn-next"
        >
          Suivant <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
